import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Star, AlertTriangle, Download, Plus, Calendar, FileText, 
  AlertOctagon, MessageSquare, User, Clock, ChevronRight
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const RATING_LABELS = {
  5: { label: "Exceptional", color: "bg-green-500" },
  4: { label: "Exceeds Expectations", color: "bg-green-400" },
  3: { label: "Meets Expectations", color: "bg-yellow-400" },
  2: { label: "Needs Improvement", color: "bg-orange-400" },
  1: { label: "Unsatisfactory", color: "bg-red-500" }
};

const DISCIPLINARY_COLORS = {
  verbal_warning: "bg-yellow-100 text-yellow-800 border-yellow-300",
  written_warning: "bg-orange-100 text-orange-800 border-orange-300",
  final_warning: "bg-red-100 text-red-800 border-red-300",
  suspension: "bg-red-200 text-red-900 border-red-400",
  termination: "bg-gray-800 text-white border-gray-900"
};

const StaffPerformance = ({ staffId, staffName, position }) => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('evaluations');
  
  // Evaluation state
  const [evaluations, setEvaluations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [evalDialogOpen, setEvalDialogOpen] = useState(false);
  const [evalForm, setEvalForm] = useState({
    evaluation_date: new Date().toISOString().split('T')[0],
    evaluation_period: '',
    ratings: {},
    strengths: '',
    areas_for_improvement: '',
    goals: '',
    employee_comments: ''
  });
  
  // Disciplinary state
  const [disciplinaryActions, setDisciplinaryActions] = useState([]);
  const [disciplinaryTypes, setDisciplinaryTypes] = useState([]);
  const [violationCategories, setViolationCategories] = useState([]);
  const [discDialogOpen, setDiscDialogOpen] = useState(false);
  const [discForm, setDiscForm] = useState({
    action_type: 'verbal_warning',
    action_date: new Date().toISOString().split('T')[0],
    violation_category: '',
    description: '',
    corrective_action: '',
    follow_up_date: '',
    suspension_dates: { start: '', end: '' },
    witness_name: '',
    employee_response: ''
  });
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (staffId) {
      fetchData();
    }
  }, [staffId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [evalRes, catRes, discRes, typesRes] = await Promise.all([
        axios.get(`${API}/staff/${staffId}/evaluations`, getAuthHeaders()),
        axios.get(`${API}/staff/evaluation-categories`, getAuthHeaders()),
        axios.get(`${API}/staff/${staffId}/disciplinary`, getAuthHeaders()).catch(() => ({ data: { disciplinary_actions: [] } })),
        axios.get(`${API}/staff/disciplinary-types`, getAuthHeaders())
      ]);
      
      setEvaluations(evalRes.data.evaluations || []);
      setCategories(catRes.data.categories || []);
      setDisciplinaryActions(discRes.data.disciplinary_actions || []);
      setDisciplinaryTypes(typesRes.data.disciplinary_types || []);
      setViolationCategories(typesRes.data.violation_categories || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvaluation = async () => {
    try {
      await axios.post(`${API}/staff/${staffId}/evaluations`, evalForm, getAuthHeaders());
      toast.success('Performance evaluation created');
      setEvalDialogOpen(false);
      setEvalForm({
        evaluation_date: new Date().toISOString().split('T')[0],
        evaluation_period: '',
        ratings: {},
        strengths: '',
        areas_for_improvement: '',
        goals: '',
        employee_comments: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create evaluation');
    }
  };

  const handleDownloadEvaluation = async (evalId) => {
    try {
      const response = await axios.post(
        `${API}/staff/${staffId}/evaluations/${evalId}/pdf`,
        {},
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Evaluation_${staffName}_${evalId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Evaluation PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleCreateDisciplinary = async () => {
    try {
      await axios.post(`${API}/staff/${staffId}/disciplinary`, discForm, getAuthHeaders());
      toast.success('Disciplinary action recorded');
      setDiscDialogOpen(false);
      setDiscForm({
        action_type: 'verbal_warning',
        action_date: new Date().toISOString().split('T')[0],
        violation_category: '',
        description: '',
        corrective_action: '',
        follow_up_date: '',
        suspension_dates: { start: '', end: '' },
        witness_name: '',
        employee_response: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to record disciplinary action');
    }
  };

  const handleDownloadDisciplinary = async (actionId) => {
    try {
      const response = await axios.post(
        `${API}/staff/${staffId}/disciplinary/${actionId}/pdf`,
        {},
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Disciplinary_${staffName}_${actionId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Disciplinary form downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const calculateOverallScore = () => {
    let total = 0;
    let weight = 0;
    categories.forEach(cat => {
      if (evalForm.ratings[cat.id]) {
        total += evalForm.ratings[cat.id] * cat.weight;
        weight += cat.weight;
      }
    });
    return weight > 0 ? (total / weight).toFixed(1) : '0.0';
  };

  const getActionTypeLabel = (type) => {
    const found = disciplinaryTypes.find(t => t.id === type);
    return found ? found.label : type;
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="evaluations" data-testid="tab-evaluations">
            <Star className="w-4 h-4 mr-1" />
            Performance ({evaluations.length})
          </TabsTrigger>
          <TabsTrigger value="disciplinary" data-testid="tab-disciplinary">
            <AlertOctagon className="w-4 h-4 mr-1" />
            Disciplinary ({disciplinaryActions.length})
          </TabsTrigger>
        </TabsList>

        {/* Performance Evaluations Tab */}
        <TabsContent value="evaluations" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Performance History</h3>
            <Button size="sm" onClick={() => setEvalDialogOpen(true)} data-testid="new-evaluation-btn">
              <Plus className="w-4 h-4 mr-1" />
              New Evaluation
            </Button>
          </div>

          <ScrollArea className="h-[350px]">
            {evaluations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Star className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No evaluations yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {evaluations.sort((a, b) => new Date(b.evaluation_date) - new Date(a.evaluation_date)).map((eval_item) => (
                  <Card key={eval_item.id} className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium text-sm">{eval_item.evaluation_date}</span>
                          {eval_item.evaluation_period && (
                            <Badge variant="outline" className="text-xs">{eval_item.evaluation_period}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-muted-foreground">Overall Score:</span>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star 
                                key={star} 
                                className={`w-4 h-4 ${star <= Math.round(eval_item.overall_score) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                              />
                            ))}
                            <span className="text-sm font-medium ml-1">{eval_item.overall_score}/5</span>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">By: {eval_item.evaluator_name}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleDownloadEvaluation(eval_item.id)}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Disciplinary Actions Tab */}
        <TabsContent value="disciplinary" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium text-muted-foreground">Disciplinary History</h3>
            <Button size="sm" onClick={() => setDiscDialogOpen(true)} data-testid="new-disciplinary-btn">
              <Plus className="w-4 h-4 mr-1" />
              New Action
            </Button>
          </div>

          {/* Progressive Discipline Visual */}
          <div className="flex items-center justify-between mb-4 p-3 bg-muted/50 rounded-lg">
            {disciplinaryTypes.map((type, idx) => {
              const hasAction = disciplinaryActions.some(a => a.action_type === type.id);
              return (
                <React.Fragment key={type.id}>
                  <div className={`flex flex-col items-center ${hasAction ? 'opacity-100' : 'opacity-40'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      hasAction ? DISCIPLINARY_COLORS[type.id] : 'bg-gray-200'
                    }`}>
                      {type.level}
                    </div>
                    <span className="text-[9px] mt-1 text-center max-w-[60px]">{type.label}</span>
                  </div>
                  {idx < disciplinaryTypes.length - 1 && (
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <ScrollArea className="h-[280px]">
            {disciplinaryActions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertOctagon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No disciplinary actions recorded</p>
              </div>
            ) : (
              <div className="space-y-3">
                {disciplinaryActions.sort((a, b) => new Date(b.action_date) - new Date(a.action_date)).map((action) => (
                  <Card key={action.id} className={`p-3 border ${DISCIPLINARY_COLORS[action.action_type] || ''}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={DISCIPLINARY_COLORS[action.action_type]}>
                            {getActionTypeLabel(action.action_type)}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{action.action_date}</span>
                        </div>
                        <p className="text-sm font-medium">{action.violation_category}</p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{action.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">Issued by: {action.issued_by}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleDownloadDisciplinary(action.id)}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>
      </Tabs>

      {/* New Evaluation Dialog */}
      <Dialog open={evalDialogOpen} onOpenChange={setEvalDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Performance Evaluation</DialogTitle>
            <DialogDescription>Evaluate {staffName}'s performance</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Evaluation Date</Label>
                <Input 
                  type="date" 
                  value={evalForm.evaluation_date}
                  onChange={(e) => setEvalForm({ ...evalForm, evaluation_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Evaluation Period</Label>
                <Input 
                  placeholder="e.g., Q1 2026, Annual 2025"
                  value={evalForm.evaluation_period}
                  onChange={(e) => setEvalForm({ ...evalForm, evaluation_period: e.target.value })}
                />
              </div>
            </div>

            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex justify-between items-center mb-3">
                <Label className="text-base">Performance Ratings</Label>
                <Badge variant="secondary" className="text-lg">{calculateOverallScore()}/5</Badge>
              </div>
              <div className="space-y-2">
                {categories.map((cat) => (
                  <div key={cat.id} className="flex items-center justify-between p-2 bg-white rounded border">
                    <span className="text-sm">{cat.label}</span>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => setEvalForm({ 
                            ...evalForm, 
                            ratings: { ...evalForm.ratings, [cat.id]: rating } 
                          })}
                          className={`w-8 h-8 rounded text-xs font-bold transition-all ${
                            evalForm.ratings[cat.id] === rating 
                              ? RATING_LABELS[rating].color + ' text-white' 
                              : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                          title={RATING_LABELS[rating].label}
                        >
                          {rating}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Strengths</Label>
              <Textarea 
                placeholder="Employee's key strengths..."
                value={evalForm.strengths}
                onChange={(e) => setEvalForm({ ...evalForm, strengths: e.target.value })}
              />
            </div>

            <div>
              <Label>Areas for Improvement</Label>
              <Textarea 
                placeholder="Areas that need development..."
                value={evalForm.areas_for_improvement}
                onChange={(e) => setEvalForm({ ...evalForm, areas_for_improvement: e.target.value })}
              />
            </div>

            <div>
              <Label>Goals for Next Period</Label>
              <Textarea 
                placeholder="Goals and objectives..."
                value={evalForm.goals}
                onChange={(e) => setEvalForm({ ...evalForm, goals: e.target.value })}
              />
            </div>

            <div>
              <Label>Employee Comments</Label>
              <Textarea 
                placeholder="Employee's response or comments..."
                value={evalForm.employee_comments}
                onChange={(e) => setEvalForm({ ...evalForm, employee_comments: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEvalDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateEvaluation} data-testid="save-evaluation-btn">
              <FileText className="w-4 h-4 mr-2" />
              Save Evaluation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Disciplinary Action Dialog */}
      <Dialog open={discDialogOpen} onOpenChange={setDiscDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Disciplinary Action</DialogTitle>
            <DialogDescription>Record disciplinary action for {staffName}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Action Type</Label>
                <Select value={discForm.action_type} onValueChange={(v) => setDiscForm({ ...discForm, action_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {disciplinaryTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${DISCIPLINARY_COLORS[type.id]?.split(' ')[0]}`}></span>
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date of Action</Label>
                <Input 
                  type="date" 
                  value={discForm.action_date}
                  onChange={(e) => setDiscForm({ ...discForm, action_date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Violation Category</Label>
              <Select value={discForm.violation_category} onValueChange={(v) => setDiscForm({ ...discForm, violation_category: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {violationCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description of Incident/Violation</Label>
              <Textarea 
                placeholder="Detailed description of what occurred..."
                value={discForm.description}
                onChange={(e) => setDiscForm({ ...discForm, description: e.target.value })}
                rows={4}
              />
            </div>

            <div>
              <Label>Corrective Action Required</Label>
              <Textarea 
                placeholder="What the employee must do to correct this issue..."
                value={discForm.corrective_action}
                onChange={(e) => setDiscForm({ ...discForm, corrective_action: e.target.value })}
              />
            </div>

            {discForm.action_type === 'suspension' && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-red-50 rounded-lg border border-red-200">
                <div>
                  <Label>Suspension Start Date</Label>
                  <Input 
                    type="date"
                    value={discForm.suspension_dates.start}
                    onChange={(e) => setDiscForm({ 
                      ...discForm, 
                      suspension_dates: { ...discForm.suspension_dates, start: e.target.value } 
                    })}
                  />
                </div>
                <div>
                  <Label>Suspension End Date</Label>
                  <Input 
                    type="date"
                    value={discForm.suspension_dates.end}
                    onChange={(e) => setDiscForm({ 
                      ...discForm, 
                      suspension_dates: { ...discForm.suspension_dates, end: e.target.value } 
                    })}
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Follow-up Date</Label>
                <Input 
                  type="date"
                  value={discForm.follow_up_date}
                  onChange={(e) => setDiscForm({ ...discForm, follow_up_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Witness Name (optional)</Label>
                <Input 
                  placeholder="Name of witness"
                  value={discForm.witness_name}
                  onChange={(e) => setDiscForm({ ...discForm, witness_name: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Employee Response</Label>
              <Textarea 
                placeholder="Employee's response or comments..."
                value={discForm.employee_response}
                onChange={(e) => setDiscForm({ ...discForm, employee_response: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateDisciplinary} variant="destructive" data-testid="save-disciplinary-btn">
              <AlertOctagon className="w-4 h-4 mr-2" />
              Record Action
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffPerformance;
